/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getMyAlbo, submitAlbo } from '@/lib/security';

const INITIAL = { loading: true, verified: false, number: '', province: '', profession: 'medico', error: null, submittedAt: null };

const fromApi = (r) => ({
  loading: false,
  verified: !!r.albo_verified,
  number: r.albo_number || '',
  province: r.albo_province || '',
  profession: r.profession || 'medico',
  error: r.albo_verification_error,
  submittedAt: r.albo_verified_at,
});

/** Albo (professional registry) verification state + submit action. */
export const useAlboStatus = () => {
  const { t } = useTranslation();
  const [state, setState] = useState(INITIAL);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setState(fromApi(await getMyAlbo()));
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = (partial) => setState((s) => ({ ...s, ...partial }));

  const submit = async () => {
    setSaving(true);
    try {
      const res = await submitAlbo({ albo_number: state.number, albo_province: state.province, profession: state.profession });
      if (res.verified) toast.success(t('alboVerifiedSuccess'));
      else toast.error(res.reason || t('alboVerifiedFailed'));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('genericError'));
    } finally {
      setSaving(false);
    }
  };

  return { state, patch, saving, submit, t };
};
